import * as React from 'react';
import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import type { Matter } from '../types';

interface NpsRatingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matter: Matter | null;
  onSubmit: (nps: number) => void;
  isLoading?: boolean;
}

function getNpsCategory(score: number): 'promoter' | 'passive' | 'detractor' {
  if (score >= 9) return 'promoter';
  if (score >= 7) return 'passive';
  return 'detractor';
}

function getButtonClasses(score: number, selected: number | null): string {
  const base = 'w-10 h-10 rounded-full border-2 font-semibold text-sm transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ';
  const isSelected = selected === score;
  const category = getNpsCategory(score);

  if (category === 'promoter') {
    return base + (isSelected
      ? 'bg-green-500 border-green-500 text-white scale-110 shadow-md'
      : 'border-green-300 text-green-700 hover:bg-green-100 focus-visible:ring-green-400');
  }
  if (category === 'passive') {
    return base + (isSelected
      ? 'bg-yellow-400 border-yellow-400 text-white scale-110 shadow-md'
      : 'border-yellow-300 text-yellow-700 hover:bg-yellow-100 focus-visible:ring-yellow-400');
  }
  return base + (isSelected
    ? 'bg-red-500 border-red-500 text-white scale-110 shadow-md'
    : 'border-red-300 text-red-700 hover:bg-red-100 focus-visible:ring-red-400');
}

export function NpsRatingDialog({
  open,
  onOpenChange,
  matter: _matter,
  onSubmit,
  isLoading,
}: NpsRatingDialogProps) {
  const { t } = useTranslation();
  const [selected, setSelected] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (open) setSelected(null);
  }, [open]);

  const handleSubmit = () => {
    if (selected !== null) onSubmit(selected);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[600px]!">
        <DialogHeader>
          <DialogTitle>{t('nps.dialogTitle')}</DialogTitle>
          <DialogDescription className="text-base font-medium text-slate-700 mt-1">
            {t('nps.question')}
          </DialogDescription>
        </DialogHeader>

        {/* 1–10 rating buttons */}
        <div className="mt-2">
          <div className="flex items-center justify-center gap-1.5 flex-wrap">
            {Array.from({ length: 10 }, (_, i) => i + 1).map((score) => (
              <button
                key={score}
                type="button"
                onClick={() => setSelected(score)}
                className={getButtonClasses(score, selected)}
                aria-label={`${score}`}
                aria-pressed={selected === score}
              >
                {score}
              </button>
            ))}
          </div>
          <div className="flex justify-between mt-2 text-xs text-muted-foreground px-1">
            <span>{t('nps.notLikely')}</span>
            <span>{t('nps.extremelyLikely')}</span>
          </div>

          {/* Category legend */}
          <div className="flex justify-center gap-5 mt-3 text-xs">
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-full bg-red-400" />
              {t('nps.detractor')} (1–6)
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-full bg-yellow-400" />
              {t('nps.passive')} (7–8)
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-full bg-green-500" />
              {t('nps.promoter')} (9–10)
            </span>
          </div>
        </div>

        {/* K2 Formula */}
        <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600 space-y-0.5">
          <p className="font-semibold text-slate-700 mb-1">{t('nps.formulaTitle')}</p>
          <p><span className="font-mono">NPS = % Promoters − % Detractors</span></p>
          <p><span className="font-mono">K2_norm = (NPS + 100) / 200</span></p>
          <p className="text-slate-400 mt-1 italic">{t('nps.formulaNote')}</p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            {t('cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={selected === null || isLoading}
          >
            {isLoading ? t('processing') : t('nps.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
